import { LocationSelector } from "@/components/LocationSelector";
import { MarketImageUploader } from "@/components/MarketImageUploader";
import { SignInRequired } from "@/components/SignInRequired";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { useAuth } from "@/contexts/AuthContext";
import { useRegion } from "@/contexts/RegionContext";
import { useToast } from "@/hooks/use-toast";
import { queryClient } from "@/lib/queryClient";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  meetingCategories,
  type InsertMeeting,
  type Meeting,
} from "@shared/schema";
import { useMutation, useQuery } from "@tanstack/react-query";
import { ArrowLeft, Loader2, Users } from "lucide-react";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { useLocation, useParams } from "wouter";
import { z } from "zod";
import { inferZoneIdFromLocation } from "@shared/zones";

const CREATE_POST_DRAFT_KEY = "ozk_local_create_post_draft_v1";

// schema.ts의 meetingCategories 사용 (통일)
const CATEGORIES = meetingCategories.map((cat) => ({
  value: cat.id,
  label: cat.label,
}));

const SCHEDULES = [
  { value: "one-time", label: "일회성" },
  { value: "weekly", label: "매주" },
  { value: "biweekly", label: "격주" },
  { value: "monthly", label: "매월" },
];

type MeetingWithAge = Meeting & { ageRange?: string | null };
type MeetingInsertPayload = InsertMeeting & {
  ageRange?: string | null;
  capacity?: number;
  coverImage?: string;
  location?: string;
  hostId?: string;
  hostName?: string;
  hostEmail?: string;
  zone_id?: string;
  zoneId?: string;
};

const formSchema = z.object({
  title: z.string().min(1, "제목을 입력하세요"),
  description: z.string().min(10, "설명을 10자 이상 입력하세요"),
  category: z.string(),
  ageRange: z.string().max(50).optional(),
  state: z.string().optional(), // LocationSelector에서 선택한 주
  suburb: z.string().optional(), // LocationSelector에서 선택한 지역
  location: z.string().optional(), // 구체적인 장소 (직접 입력)
  schedule: z.string(),
  capacity: z.string().optional(),
  fee: z.string().optional(),
  coverImage: z.string().optional(),
  // photos 필드 제거: 모임 생성 시에는 대표 이미지만 업로드하고, 나중에 사진갤러리에서 추가
  hostId: z.string(),
  hostName: z.string(),
  hostEmail: z.string().optional(),
});

export default function MeetingForm() {
  const { id } = useParams<{ id?: string }>();
  const [, navigate] = useLocation();
  const { toast } = useToast();
  const { user, isAuthenticated } = useAuth();
  const { selectedRegion, selectedSuburb } = useRegion();

  const isEditing = Boolean(id);

  const { data: meeting } = useQuery<MeetingWithAge>({
    queryKey: ["/api/meetings", id],
    enabled: isEditing,
  });

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      title: "",
      description: "",
      category: "hobby", // 기본값을 hobby로 변경 (schema에 맞춤)
      ageRange: "",
      state:
        selectedRegion && selectedRegion !== "ALL" ? selectedRegion : "NSW",
      suburb: selectedSuburb || "",
      location: "",
      schedule: "weekly",
      capacity: "",
      fee: "",
      coverImage: "",
      // photos 필드 제거: 모임 생성 시에는 대표 이미지만
      hostId: user?.uid || "",
      hostName: user?.displayName || user?.email || "",
      hostEmail: user?.email || "",
    },
  });

  useEffect(() => {
    if (isEditing) return;

    try {
      const rawDraft = sessionStorage.getItem(CREATE_POST_DRAFT_KEY);
      if (!rawDraft) return;

      const draft = JSON.parse(rawDraft);
      if (draft?.type !== "meeting") return;

      form.reset({
        title: typeof draft.title === "string" ? draft.title : "",
        description:
          typeof draft.description === "string" ? draft.description : "",
        category: typeof draft.category === "string" ? draft.category : "hobby",
        ageRange: "",
        state: typeof draft.state === "string" ? draft.state : "NSW",
        suburb: typeof draft.suburb === "string" ? draft.suburb : "",
        location: typeof draft.location === "string" ? draft.location : "",
        schedule: typeof draft.schedule === "string" ? draft.schedule : "one-time",
        capacity: typeof draft.capacity === "string" ? draft.capacity : "",
        fee: "",
        coverImage: "",
        hostId: user?.uid || "",
        hostName: user?.displayName || user?.email || "",
        hostEmail: user?.email || "",
      });
      sessionStorage.removeItem(CREATE_POST_DRAFT_KEY);
    } catch (error) {
      console.warn("[MeetingForm] Failed to load create draft", error);
    }
  }, [form, isEditing, user?.displayName, user?.email, user?.uid]);

  // Update form when meeting data loads
  if (isEditing && meeting && !form.formState.isDirty) {
    form.reset({
      title: meeting.title,
      description: meeting.description,
      category: meeting.category,
      ageRange: meeting?.ageRange || "",
      state:
        meeting.state ||
        (selectedRegion && selectedRegion !== "ALL" ? selectedRegion : "NSW"),
      suburb: meeting.suburb || selectedSuburb || "",
      location: meeting.location || "",
      schedule: meeting.schedule,
      capacity: meeting.capacity?.toString() || "",
      fee: meeting.fee || "",
      coverImage: meeting.coverImage || "",
      // photos 필드 제거: 모임 생성 시에는 대표 이미지만
      hostId: meeting.hostId,
      hostName: meeting.hostName,
      hostEmail: meeting.hostEmail || "",
    });
  }

  // LocationSelector에서 선택한 지역이 변경되면 폼 값도 업데이트 (새 모임 생성 시에만)
  useEffect(() => {
    if (!isEditing) {
      const newState =
        selectedRegion && selectedRegion !== "ALL" ? selectedRegion : "NSW";
      const newSuburb = selectedSuburb || "";
      form.setValue("state", newState);
      form.setValue("suburb", newSuburb);
    }
  }, [selectedRegion, selectedSuburb, isEditing, form]);

  const createMutation = useMutation({
    mutationFn: async (data: MeetingInsertPayload) => {
      const { getIdToken } = await import("@/lib/firebase");
      const token = await getIdToken();

      if (!token) {
        throw new Error("로그인이 필요합니다");
      }

      const response = await fetch("/api/meetings", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(data),
      });
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || "Failed to create meeting");
      }
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/meetings"] });
      toast({
        title: "모임 생성 완료",
        description: "새로운 모임이 생성되었습니다.",
      });
      navigate("/meetings");
    },
    onError: (error: Error) => {
      toast({
        title: "모임 생성 실패",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const updateMutation = useMutation({
    mutationFn: async (data: Partial<MeetingInsertPayload>) => {
      const { getIdToken } = await import("@/lib/firebase");
      const token = await getIdToken();

      if (!token) {
        throw new Error("로그인이 필요합니다");
      }

      const response = await fetch(`/api/meetings/${id}`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(data),
      });
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || "Failed to update meeting");
      }
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/meetings"] });
      queryClient.invalidateQueries({ queryKey: ["/api/meetings", id] });
      toast({
        title: "모임 수정 완료",
        description: "모임 정보가 업데이트되었습니다.",
      });
      navigate(`/meetings/${id}`);
    },
    onError: (error: Error) => {
      toast({
        title: "모임 수정 실패",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const onSubmit = async (values: z.infer<typeof formSchema>) => {
    if (!isAuthenticated || !user) {
      toast({
        title: "로그인 필요",
        description: "모임을 만들려면 로그인해야 합니다.",
        variant: "destructive",
      });
      return;
    }

    const zoneId = inferZoneIdFromLocation({
      suburb: values.suburb,
      state: values.state,
    });

    const meetingData: MeetingInsertPayload = {
      ...values,
      ageRange: values.ageRange?.trim() ? values.ageRange.trim() : undefined,
      capacity: values.capacity ? parseInt(values.capacity) : undefined,
      coverImage: values.coverImage || undefined,
      // photos 필드 제거: 모임 생성 시에는 대표 이미지만, 나중에 사진갤러리에서 추가
      location: values.location || undefined, // 상세 주소 추가
      zone_id: zoneId,
      zoneId,
      hostId: user.uid,
      hostName: user.displayName || user.email || "익명",
      hostEmail: user.email || "",
    };

    if (isEditing) {
      updateMutation.mutate(meetingData);
    } else {
      createMutation.mutate(meetingData);
    }
  };

  if (!isAuthenticated) {
    return (
      <SignInRequired
        title="교민모임 만들기"
        description="주최자 정보와 참여 문의 알림을 연결하기 위해 로그인해주세요. 로그인 후 이 화면에서 일정을 계속 작성할 수 있습니다."
        backHref="/meetings"
        backLabel="교민모임 목록"
      />
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-4xl mx-auto px-4 py-8">
        <Button
          variant="ghost"
          className="mb-4"
          onClick={() => navigate("/meetings")}
          data-testid="button-back"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          모임 목록으로
        </Button>

        <Card className="p-6">
          <div className="mb-6">
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <Users className="w-6 h-6 text-[#663366] dark:text-[#9966cc]" />
              {isEditing ? "모임 수정" : "새 모임 만들기"}
            </h1>
            <p className="text-muted-foreground mt-2">
              우리 지역에서 새로운 친구들과 함께할 모임을 만들어보세요.
            </p>
          </div>

          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
              <FormField
                control={form.control}
                name="title"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>모임 이름 *</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="예: 시드니 한국어 스터디 모임"
                        {...field}
                        data-testid="input-title"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="description"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>모임 소개 *</FormLabel>
                    <FormControl>
                      <Textarea
                        placeholder="모임에 대해 자세히 설명해주세요..."
                        className="min-h-32"
                        {...field}
                        data-testid="input-description"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <FormField
                  control={form.control}
                  name="category"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>카테고리 *</FormLabel>
                      <Select
                        onValueChange={field.onChange}
                        defaultValue={field.value}
                      >
                        <FormControl>
                          <SelectTrigger data-testid="select-category">
                            <SelectValue placeholder="카테고리 선택" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {CATEGORIES.map((cat) => {
                            const categoryInfo = meetingCategories.find(
                              (c) => c.id === cat.value
                            );
                            return (
                              <SelectItem key={cat.value} value={cat.value}>
                                {categoryInfo?.icon} {cat.label}
                              </SelectItem>
                            );
                          })}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="schedule"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>모임 일정 *</FormLabel>
                      <Select
                        onValueChange={field.onChange}
                        defaultValue={field.value}
                      >
                        <FormControl>
                          <SelectTrigger data-testid="select-schedule">
                            <SelectValue placeholder="일정 선택" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {SCHEDULES.map((sched) => (
                            <SelectItem key={sched.value} value={sched.value}>
                              {sched.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="ageRange"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>참여 권장 나이대</FormLabel>
                      <FormControl>
                        <Input
                          placeholder="예: 2030, 1020, 전연령"
                          {...field}
                          data-testid="input-age-range"
                        />
                      </FormControl>
                      <FormDescription>
                        모임에서 선호하는 나이대가 있다면 입력하세요. (예: 2030)
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              {/* 지역 선택 (네비게이션과 동일한 방식) */}
              <FormField
                control={form.control}
                name="state"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>지역 *</FormLabel>
                    <FormControl>
                      <div className="space-y-2">
                        <LocationSelector variant="button" />
                        <p className="text-xs text-muted-foreground">
                          현재 선택된 지역:{" "}
                          {selectedRegion && selectedRegion !== "ALL"
                            ? `${
                                selectedSuburb ? `${selectedSuburb}, ` : ""
                              }${selectedRegion}`
                            : "지역을 선택해주세요"}
                        </p>
                      </div>
                    </FormControl>
                    <FormDescription>
                      네비게이션에서 설정한 내 주 지역이 기본값으로 사용됩니다.
                      클릭하여 변경할 수 있습니다.
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* 구체적인 장소 (직접 입력) */}
              <FormField
                control={form.control}
                name="location"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>구체적인 장소</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="예: Strathfield Library, Sydney Olympic Park, 카페 이름 등"
                        {...field}
                        value={field.value || ""}
                        data-testid="input-location"
                      />
                    </FormControl>
                    <FormDescription>
                      모임이 열리는 구체적인 장소를 입력하세요 (예: 도서관,
                      공원, 카페 이름 등)
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <FormField
                  control={form.control}
                  name="capacity"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>최대 인원</FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          placeholder="제한 없음"
                          {...field}
                          data-testid="input-capacity"
                        />
                      </FormControl>
                      <FormDescription>
                        비워두면 인원 제한이 없습니다
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="fee"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>참가비</FormLabel>
                      <FormControl>
                        <Input
                          placeholder="무료 또는 $10"
                          {...field}
                          value={field.value || ""}
                          data-testid="input-fee"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              {/* Cover Image */}
              <FormField
                control={form.control}
                name="coverImage"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>대표 이미지</FormLabel>
                    <FormControl>
                      <div className="space-y-2">
                        <MarketImageUploader
                          maxImages={1}
                          onImagesChange={(paths) => {
                            field.onChange(paths[0] || "");
                          }}
                          userId={user?.uid}
                          initialImages={field.value ? [field.value] : []}
                        />
                      </div>
                    </FormControl>
                    <FormDescription>
                      모임을 대표하는 이미지를 업로드하세요 (선택사항). 추가
                      사진은 모임 생성 후 사진 갤러리에서 업로드할 수 있습니다.
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="flex gap-4 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => navigate("/meetings")}
                  data-testid="button-cancel"
                >
                  취소
                </Button>
                <Button
                  type="submit"
                  disabled={
                    createMutation.isPending || updateMutation.isPending
                  }
                  data-testid="button-submit"
                >
                  {(createMutation.isPending || updateMutation.isPending) && (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  )}
                  {isEditing ? "수정하기" : "모임 만들기"}
                </Button>
              </div>
            </form>
          </Form>
        </Card>
      </div>
    </div>
  );
}
